import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { createSlice } from "@reduxjs/toolkit";
import { getStore } from "../../storeRef";

//--------------------------------------------------------------------------------------
//  For redux service for applciation scoped (data wont change in application lifetime)
//--------------------------------------------------------------------------------------
export function createApplicationService({
  name,
  apiFetchFn,
  apiAddFn,
  apiUpdateFn,
}) {
  const slice = createSlice({
    name,
    initialState: {
      data: [],
      fetched: false,
    },
    reducers: {
      setData(state, action) {
        state.data = action.payload;
        state.fetched = true;
      },
      resetFetched(state) {
        state.fetched = false;
      },
    },
  });

  const { setData } = slice.actions;
  const reducer = slice.reducer;

  const _fetchData = () => async (dispatch) => {
    try {
      const result = await apiFetchFn();
      dispatch(setData(result.data.data));
      return result.data.data;
    } catch (error) {}
  };
  let inProgress = false;
  /** Shared by concurrent getData() callers until the first fetch settles */
  let fetchPromise = null;

  const getData = async () => {
    const store = getStore();
    const state = store.getState();
    if (!state[name].fetched) {
      if (!inProgress) {
        inProgress = true;
        fetchPromise = store.dispatch(_fetchData()).finally(() => {
          inProgress = false;
          fetchPromise = null;
        });
      }
      // Await the same promise as every other in-flight caller (replacing setTimeout(100))
      await fetchPromise;
    }
    return getStore().getState()[name].data;
  };

  const addData = async (data) => {
    try {
      await apiAddFn(data);
      getStore().dispatch(_fetchData());
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  };

  const updateData = async (id, data) => {
    try {
      await apiUpdateFn(id, data);
      getStore().dispatch(_fetchData());
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  };

  const useData = () => {
    const data = useSelector((state) => state[name].data);
    const fetched = useSelector((state) => state[name].fetched);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
      if (!fetched) {
        setLoading(true);
        getData().finally(() => setLoading(false));
      }
    }, [fetched]);
    return { data, loading };
  };

  const resetData = () => {
    getStore().dispatch(slice.actions.resetFetched());
  };

  return {
    reducer,
    getData,
    addData,
    updateData,
    useData,
    resetData,
  };
}
